import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Link,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EmailIcon from "@mui/icons-material/Email";
import { IMG_HOODIE_BLACK_01, IMG_DAINASOUL_CANVAS_TOTE_BLACK } from "./imageConfig";

const ReturnOrder = () => {
  const [step, setStep] = useState(1);
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [orderError, setOrderError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [language, setLanguage] = useState("en");

  // Step 2 state: Items selection
  const [selectedItems, setSelectedItems] = useState({
    item1: false,
    item2: false,
  });
  const [returnReasons, setReturnReasons] = useState({
    item1: "",
    item2: "",
  });

  // Step 3 state: Return Method
  const [returnMethod, setReturnMethod] = useState("dropoff");

  // Mock Order Items data
  const mockItems = [
    {
      id: "item1",
      name: "Dainasoul Signature Hoodie",
      variant: "Jet Black / L",
      price: "$89.00",
      image: IMG_HOODIE_BLACK_01,
    },
    {
      id: "item2",
      name: "Dainasoul Canvas Tote",
      variant: "Classic Black / One Size",
      price: "$29.00",
      image: IMG_DAINASOUL_CANVAS_TOTE_BLACK,
    },
  ];

  const handleStart = () => {
    let hasError = false;
    setOrderError("");
    setEmailError("");

    if (!orderNumber) {
      setOrderError("Please enter an order number.");
      hasError = true;
    }
    if (!email) {
      setEmailError("Please enter an email address.");
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Please enter a valid email address.");
      hasError = true;
    }

    if (!hasError) {
      // Proceed to item selection
      setStep(2);
    }
  };

  const handleItemToggle = (itemId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleReasonChange = (itemId, reason) => {
    setReturnReasons((prev) => ({
      ...prev,
      [itemId]: reason,
    }));
  };

  const handleNextToMethod = () => {
    // Check if at least one item is selected
    const anySelected = Object.values(selectedItems).some((v) => v);
    if (!anySelected) {
      alert("Please select at least one item to return.");
      return;
    }

    // Verify reason selected for all selected items
    let reasonsOk = true;
    mockItems.forEach((item) => {
      if (selectedItems[item.id] && !returnReasons[item.id]) {
        reasonsOk = false;
      }
    });

    if (!reasonsOk) {
      alert("Please choose a reason for each selected item.");
      return;
    }

    setStep(3);
  };

  const handleSubmitReturn = () => {
    setStep(4);
  };

  const generateRMANumber = () => {
    return "RMA-" + Math.floor(100000 + Math.random() * 90000);
  };

  const rmaNumber = React.useMemo(() => generateRMANumber(), []);

  // Custom CSS Barcode representation for extreme high-fidelity mock return label
  const Barcode = () => {
    const barWidths = [2, 4, 1, 3, 2, 4, 1, 2, 3, 1, 4, 2, 1, 3, 2, 4, 1, 2, 3, 2, 1, 4, 3, 1, 2, 4, 1];
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", my: 2 }}>
        {barWidths.map((width, idx) => (
          <Box
            key={idx}
            sx={{
              width: `${width}px`,
              height: "50px",
              backgroundColor: idx % 2 === 0 ? "#000" : "transparent",
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pt: "140px",
        pb: "100px",
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            p: 4,
            position: "relative",
          }}
        >
          {/* Back Navigation for middle steps */}
          {step > 1 && step < 4 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setStep(step - 1)}
              sx={{
                position: "absolute",
                top: 24,
                left: 24,
                color: "#666",
                textTransform: "none",
                fontSize: "14px",
                "&:hover": { color: "#000", background: "none" },
              }}
            >
              Back
            </Button>
          )}

          {/* Language Selector (Authentic representation of Vogue screenshot) */}
          {step === 1 && (
            <Box
              sx={{
                position: "absolute",
                top: 24,
                right: 24,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  px: 1,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                }}
              >
                {/* Globe/Flag simulation */}
                <Typography sx={{ fontSize: "14px" }}>🇬🇧 English</Typography>
                <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#666" }} />
              </Box>
            </Box>
          )}

          <CardContent sx={{ p: 0, mt: step > 1 ? 4 : 0 }}>
            {/* STEP 1: Portal Entry Gate */}
            {step === 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                {/* DAINASOUL Logo */}
                <Typography
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    fontSize: "28px",
                    letterSpacing: "4px",
                    textTransform: "uppercase",
                    color: "#000",
                    mb: 1,
                  }}
                >
                  DAINASOUL
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    textAlign: "center",
                    color: "#222",
                  }}
                >
                  Return your order
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    textAlign: "center",
                    mb: 1,
                  }}
                >
                  To start enter your Order No and Email.
                </Typography>

                <TextField
                  fullWidth
                  label="Order No."
                  variant="outlined"
                  value={orderNumber}
                  error={!!orderError}
                  helperText={orderError}
                  onChange={(e) => {
                    setOrderNumber(e.target.value);
                    if (e.target.value) setOrderError("");
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  value={email}
                  error={!!emailError}
                  helperText={emailError}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (e.target.value) setEmailError("");
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                    },
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleStart}
                  sx={{
                    backgroundColor: orderNumber && email ? "#000" : "#d3d3d3",
                    color: "#fff",
                    borderRadius: "4px",
                    py: 1.5,
                    fontSize: "15px",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: orderNumber && email ? "#333" : "#d3d3d3",
                      boxShadow: "none",
                    },
                  }}
                >
                  Start
                </Button>

                <Link
                  href="/return-policy"
                  target="_blank"
                  sx={{
                    color: "#000",
                    fontSize: "14px",
                    textDecoration: "underline",
                    mt: 2,
                    fontWeight: 500,
                  }}
                >
                  Returns Policy
                </Link>
              </Box>
            )}

            {/* STEP 2: Item Selection */}
            {step === 2 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#222" }}>
                  Select items to return
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Please select the items you wish to return and provide a reason.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5, mt: 1 }}>
                  {mockItems.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        p: 2,
                        backgroundColor: selectedItems[item.id] ? "#fafafa" : "#fff",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Checkbox
                          checked={selectedItems[item.id]}
                          onChange={() => handleItemToggle(item.id)}
                          sx={{ color: "#000", "&.Mui-checked": { color: "#000" } }}
                        />
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.name}
                          sx={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            backgroundColor: "#f9f9f9",
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: "#777", fontSize: "13px" }}>
                            {item.variant}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>
                          {item.price}
                        </Typography>
                      </Box>

                      {selectedItems[item.id] && (
                        <Box sx={{ mt: 2, pl: 5 }}>
                          <FormControl fullWidth size="small">
                            <Typography sx={{ fontSize: "13px", mb: 0.5, fontWeight: 500 }}>
                              Reason for return*
                            </Typography>
                            <Select
                              value={returnReasons[item.id]}
                              onChange={(e) => handleReasonChange(item.id, e.target.value)}
                              displayEmpty
                              sx={{ borderRadius: "4px" }}
                            >
                              <MenuItem value="" disabled>
                                Choose return reason...
                              </MenuItem>
                              <MenuItem value="size_small">Size too small</MenuItem>
                              <MenuItem value="size_large">Size too large</MenuItem>
                              <MenuItem value="defective">Defective / Damaged</MenuItem>
                              <MenuItem value="wrong_item">Received wrong item</MenuItem>
                              <MenuItem value="not_described">Item not as described</MenuItem>
                              <MenuItem value="changed_mind">Changed mind / Don't want</MenuItem>
                            </Select>
                          </FormControl>
                          {returnReasons[item.id] === "defective" && (
                            <Typography sx={{ fontSize: "11px", color: "#d32f2f", mt: 1 }}>
                              Note: If the item was received damaged, you may also email us directly
                              at contact@dainasoul.com for an immediate exchange.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleNextToMethod}
                  sx={{
                    backgroundColor: "#000",
                    color: "#fff",
                    borderRadius: "4px",
                    py: 1.5,
                    fontSize: "15px",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "none",
                    mt: 2,
                    "&:hover": {
                      backgroundColor: "#333",
                      boxShadow: "none",
                    },
                  }}
                >
                  Continue
                </Button>
              </Box>
            )}

            {/* STEP 3: Return Method */}
            {step === 3 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#222" }}>
                  Choose Return Method
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Select how you would like to return your items.
                </Typography>

                <RadioGroup
                  value={returnMethod}
                  onChange={(e) => setReturnMethod(e.target.value)}
                  sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
                >
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      p: 2,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      backgroundColor: returnMethod === "dropoff" ? "#fafafa" : "#fff",
                    }}
                  >
                    <FormControlLabel
                      value="dropoff"
                      control={<Radio sx={{ color: "#000", "&.Mui-checked": { color: "#000" } }} />}
                      label=""
                      sx={{ m: 0, pt: 0.5 }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "15px", display: "flex", alignItems: "center", gap: 1 }}>
                        <LocalShippingIcon sx={{ fontSize: "18px" }} /> Pre-paid Drop-off (Free)
                      </Typography>
                      <Typography sx={{ color: "#666", fontSize: "13px", mt: 0.5 }}>
                        Print the pre-paid label and drop your package off at any authorized shipping branch.
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      p: 2,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      backgroundColor: returnMethod === "pickup" ? "#fafafa" : "#fff",
                    }}
                  >
                    <FormControlLabel
                      value="pickup"
                      control={<Radio sx={{ color: "#000", "&.Mui-checked": { color: "#000" } }} />}
                      label=""
                      sx={{ m: 0, pt: 0.5 }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "15px", display: "flex", alignItems: "center", gap: 1 }}>
                        <EmailIcon sx={{ fontSize: "18px" }} /> Home Pickup ($4.99)
                      </Typography>
                      <Typography sx={{ color: "#666", fontSize: "13px", mt: 0.5 }}>
                        A courier will pick up the package from your address. No label printing required.
                      </Typography>
                    </Box>
                  </Box>
                </RadioGroup>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmitReturn}
                  sx={{
                    backgroundColor: "#000",
                    color: "#fff",
                    borderRadius: "4px",
                    py: 1.5,
                    fontSize: "15px",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "none",
                    mt: 2,
                    "&:hover": {
                      backgroundColor: "#333",
                      boxShadow: "none",
                    },
                  }}
                >
                  Submit Return
                </Button>
              </Box>
            )}

            {/* STEP 4: Success & Return Label */}
            {step === 4 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: "64px", color: "#4caf50" }} />

                <Typography variant="h5" sx={{ fontWeight: 600, color: "#222", textAlign: "center" }}>
                  Return Submitted!
                </Typography>

                <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>
                  Your return request was submitted successfully. We have sent a confirmation email to <strong>{email}</strong>.
                </Typography>

                <Divider sx={{ width: "100%", my: 1 }} />

                {/* Shipping Label Section */}
                <Box
                  sx={{
                    border: "2px dashed #000",
                    borderRadius: "8px",
                    p: 3,
                    width: "100%",
                    backgroundColor: "#fff",
                    textAlign: "left",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "18px", letterSpacing: "1px", fontFamily: '"Playfair Display", serif' }}>
                      DAINASOUL
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "12px", border: "1px solid #000", px: 1, py: 0.5 }}>
                      PARCEL RETURN
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: "11px", color: "#444" }}>
                    <strong>FROM:</strong>
                    <br />
                    {email}
                    <br />
                    Order Ref: #{orderNumber}
                  </Typography>

                  <Box sx={{ my: 1.5 }}>
                    <Typography sx={{ fontSize: "11px", color: "#444" }}>
                      <strong>SHIP TO:</strong>
                      <br />
                      DAINASOUL Returns Dept.
                      <br />
                      104 Logistics Blvd Suite D
                      <br />
                      Mumbai, MH, 400001
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5, borderColor: "#000" }} />

                  <Typography sx={{ fontSize: "12px", fontWeight: 700, textAlign: "center" }}>
                    RMA NUMBER: {rmaNumber}
                  </Typography>

                  {/* Render the Custom Barcode */}
                  <Barcode />

                  <Typography sx={{ fontSize: "9px", color: "#666", textAlign: "center" }}>
                    *Cut along the line above and tape securely to the outside of your return parcel.
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, width: "100%", mt: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => window.print()}
                    sx={{
                      borderColor: "#000",
                      color: "#000",
                      borderRadius: "4px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: "#333", backgroundColor: "#f9f9f9" },
                    }}
                  >
                    Print Label
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setStep(1);
                      setOrderNumber("");
                      setEmail("");
                    }}
                    sx={{
                      backgroundColor: "#000",
                      color: "#fff",
                      borderRadius: "4px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "#333" },
                    }}
                  >
                    Return Home
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Powered by tag matching Vogue Collection aesthetic */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography sx={{ fontSize: "11px", color: "#888" }}>
            Powered by{" "}
            <strong style={{ color: "#555", fontWeight: 700, letterSpacing: "1px" }}>
              8RETURNS
            </strong>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ReturnOrder;
